import { useRef, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

interface EmojiPickerPopupProps {
  onSelect: (emoji: string) => void
  onClose: () => void
  anchorRef?: React.RefObject<HTMLElement>
}

const EMOJIS = [
  '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊',
  '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘',
  '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪',
  '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒',
  '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖',
  '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡',
  '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰',
  '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶',
  '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮',
  '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴',
  '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠',
  '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙',
  '👈', '👉', '👆', '👇', '☝️', '👋', '🤚', '🖐',
  '✋', '🖖', '👏', '🙌', '👐', '🤲', '🤝', '🙏',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
  '💯', '💢', '💥', '💫', '💦', '💨', '🕳️', '💬',
]

const EmojiPickerPopup = ({ onSelect, onClose, anchorRef }: EmojiPickerPopupProps) => {
  const pickerRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  // Calculate position based on anchor element
  useEffect(() => {
    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect()
      const pickerHeight = 300 // max height
      const pickerWidth = 280

      let top = rect.top - pickerHeight - 8 // Position above button
      let left = rect.left

      // Adjust if would go off screen
      if (top < 20) {
        top = rect.bottom + 8 // Position below if no room above
      }
      if (left + pickerWidth > window.innerWidth) {
        left = window.innerWidth - pickerWidth - 20
      }

      setPosition({ top, left })
    }
  }, [anchorRef])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        (!anchorRef?.current || !anchorRef.current.contains(event.target as Node))
      ) {
        onClose()
      }
    }

    // Delay to avoid immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose, anchorRef])

  if (!position) return null

  const picker = (
    <div
      ref={pickerRef}
      className="bg-white dark:bg-dark-card border dark:border-dark-border rounded-lg shadow-lg p-3"
      style={{
        position: 'fixed',
        zIndex: 10001,
        width: '280px',
        maxHeight: '300px',
        overflowY: 'auto',
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="grid grid-cols-8 gap-1">
        {EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onSelect(emoji)
              onClose()
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-accent rounded text-xl transition-transform hover:scale-125"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  )

  return createPortal(picker, document.body)
}

export default EmojiPickerPopup
